import React, { useState } from "react";
import { motion } from "framer-motion";

export const RaceRegulations = () => {
  const [activeIndex, setActiveIndex] = useState(0); // Default to first item open

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const regulations = [
    {
      title: "PARTICIPATION REQUIREMENTS",
      content: `
        Trail running race participants must:
         <ul class="list-disc pl-5 mt-2">
          <li>Understand the duration and characteristics of the race and have prepared themselves well.</li>
          <li>Understand the unique nature and requirements of trail running, including running in mountain terrain at night or early morning, and have undergone the necessary training.</li>
          <li>Be responsible for themselves and have the ability to overcome problems that may arise during the race, such as fatigue, digestive problems, muscle or joint pain, and minor injuries.</li>
          <li>Understand that the organizer is not responsible for helping to overcome such problems, and participant safety depends on their own ability to anticipate and adapt to potential issues.</li>
        </ul>
      `,
    },

    {
      title: "REGISTRATION POLICY",
      content: `
    By registering, participants are deemed to agree to the following terms:
    <ul class="list-disc pl-5 mt-2">
      <li>Registration fees are non-refundable and cannot be cancelled.</li>
      <li>BIB transfer can be done within a certain period of time by contacting the number or email on the contact page.</li>
      <li>Using another runner's BIB is prohibited for safety and qualification reasons, and will result in disqualification.</li>
    </ul>
  `,
    },

    {
      title: "MANDATORY GEARS",
      content: `
    Participants must carry Mandatory Gears according to the race category (see on the category detail page). 
    <ul class="list-disc pl-5 mt-2">
      <li>Not carrying mandatory gear will result in a penalty.</li>
      <li>Marshals have the right to randomly check the contents of participant's vest/belt, and participants must comply with the inspection.</li>
      <li>Marshals have the right to disqualify participants who do not comply with the inspection.</li>
    </ul>
  `,
    },

    {
      title: "WATER STATIONS",
      content: `
    Food and drinks are provided at three different locations along the route, namely WS Kandang Badak, WS Surken, and WS Putri. <br/><br/>
    Water stations provide:
    <ul class="list-disc pl-5 mt-2">
      <li>Mineral water, carbonated drinks, isotonic, as well as fruits.</li>
      <li>Participants must carry a foldable cup to avoid the use of single-use plastics.</li>
    </ul>
  `,
    },

    {
      title: "MEDICAL ASSISTANCE",
      content: `
    The organizer provides medical teams at several locations to provide first aid if needed. Nevertheless, each participant remains fully responsible for their own condition and safety during the race.<br/><br/>
    The medical team is authorized to:
  <ul class="list-disc pl-5 mt-2">
      <li>Stop participants if they are deemed unfit to continue the race.</li>
      <li>Evacuate participants who are in dangerous situations.</li>
      <li>Refer participants to healthcare facilities if needed.</li>
    </ul>
  `,
    },
    {
      title: "HEALTH CERTIFICATE",
      content: `
    Participants must bring a health certificate stating that they are physically fit to participate in the race. This health certificate must be issued a maximum of 7 days before the race and can be obtained from a clinic, hospital, or doctor. Without a health certificate, participants will not receive a race number (BIB).
  `,
    },

    {
      title: "COURSE MARKING",
      content: `
        The route is marked with ribbons, signage, and other markers, with reflective parts for night visibility. Race officials will be positioned along the route and intersections. Follow the instructions of officials. Markers are installed before the race and removed afterwards. For environmental considerations, paint is not used as a marker.
      `,
    },
    {
      title: "WITHDRAWING FROM THE RACE",
      content: `
    In the situation where a participant decides or is forced to stop the race, the following terms apply:
    <ul class="list-disc pl-5 mt-2">
      <li>Participants who stop are advised to immediately report to the officials.</li>
      <li>Participants who stop are not allowed to continue the race.</li>
    </ul>
  `,
    },

    {
      title: "PROHIBITED ACTIONS",
      content: `
    Participants are prohibited from performing the following actions during the race:
    <ul class="list-disc pl-5 mt-2">
      <li>Cutting the course.</li>
      <li>Littering.</li>
      <li>Not carrying mandatory gear.</li>
      <li>Accompanying runners without registration.</li>
      <li>Cheating (e.g., using other transportation).</li>
      <li>Violating official instructions.</li>
      <li>Using doping.</li>
    </ul><br/>
    Violations of the above provisions may be subject to sanctions in the form of penalties or disqualification, according to the committee's decision.
  `,
    },

    {
      title: "RESPONSIBILITY AND RACE CANCELLATION",
      content: `
    Each participant is responsible for their own safety and any risks that may arise during the race. The organizer is not responsible for any damage to personal property that occurs beyond control.<br/><br/>
    The race may be temporarily suspended or shortened if emergency conditions occur, including but not limited to:
  <ul class="list-disc pl-5 mt-2">
      <li>Extreme weather warnings.</li>
      <li>Natural disasters (referring to official BMKG information).</li>
    </ul>
  `,
    },

    {
      title: "MEDIA RIGHTS AND PUBLICATION",
      content: `
        By participating, participants agree that:
      <ul class="list-disc pl-5 mt-2">
          <li>The organizer has the right to take and use photos, videos, race results, and other media content during and after the event for promotion, marketing, and editorial purposes on various platforms, including newspapers, television, magazines, websites, and social media.</li>
          <li>Participants provide a royalty-free, worldwide, and perpetual license to the organizer to use the images, names, and race results without compensation or additional notice.</li>
          <li>The organizer owns all rights to the official event media and can distribute content according to their policy.</li>
          <li>Participants who do not wish to be featured in event-related media must submit a written request to the organizer before the race day.</li>
        </ul>
      `,
    },
    {
      title: "COMPLAINTS AND PROTESTS",
      content: `
        All objections to the decision of the committee or the race route must be submitted in writing, accompanied by a deposit of Rp1,000,000 (non-refundable if the objection is considered invalid), within two hours after the participant arrives at the finish line. The race jury, consisting of:
      <ul class="list-disc pl-5 mt-2">
          <li>Race Jury.</li>
           <li>Race Director.</li>
          <li>ALTI Representative.</li>
          <li>Competent parties selected by the race director.</li>
        </ul> <br/>
        The race jury is authorized to make a final and binding decision.
      `,
    },
  ];

  return (
    <section
      id="race-regulations"
      className="w-full min-h-screen py-20 text-center text-lime-600 flex flex-col justify-center relative bg-white"
    >
      <div className="relative z-0 container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-6xl mx-auto space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              viewport={{ once: true }}
            >
              <div className="join join-vertical ">
                {regulations.map((reg, index) => (
                  <div
                    key={index}
                    className="collapse collapse-arrow join-item group"
                  >
                    <input
                      type="radio"
                      name="race-regulations-accordion"
                      checked={activeIndex === index}
                      onChange={() => toggleAccordion(index)}
                    />
                    <div
                      className={`collapse-title text-left text-base font-medium uppercase  transition-colors duration-200
          ${activeIndex === index ? "text-lime-600" : "text-gray-600"} 
          group-hover:text-lime-600`}
                    >
                      {reg.title}
                    </div>
                    <div
                      className="collapse-content text-left text-base text-gray-600"
                      dangerouslySetInnerHTML={{ __html: reg.content }}
                    />
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RaceRegulations;
